import React from "react";
import { usePermissions } from "../../../hooks/usePermissions";

export interface PermissionGateProps {
  permission?: string;
  anyPermission?: string[];
  allPermissions?: string[];
  module?: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Conditionally renders UI actions (buttons, dropdowns, forms) based on studio permissions.
 *
 * Example:
 * <PermissionGate permission="albums.create">
 *   <AddAlbumButton />
 * </PermissionGate>
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  anyPermission,
  allPermissions,
  module,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasModule, permissionsLoading, isSubUser } =
    usePermissions();

  // If permissions are still being fetched for a sub-user, avoid rendering until resolved
  if (permissionsLoading && isSubUser) {
    return null;
  }

  let isAllowed = true;

  if (permission) {
    isAllowed = hasPermission(permission);
  } else if (anyPermission && anyPermission.length > 0) {
    isAllowed = hasAnyPermission(anyPermission);
  } else if (allPermissions && allPermissions.length > 0) {
    isAllowed = hasAllPermissions(allPermissions);
  } else if (module) {
    isAllowed = hasModule(module);
  }

  return isAllowed ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGate;
